import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  FolderKanban,
  Users,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Bot,
  Sheet,
  BookOpen,
  Award,
  Calendar,
  Briefcase,
  Tag,
  Building2,
  UsersRound,
  Shield,
  Sparkles,
  BarChart3
} from 'lucide-react';

const navItems = [
  { path: '/agents', label: 'Carlos Mario', description: 'Request assistant', icon: Bot },
  { path: '/planner', label: 'Planner', description: 'Backlog & parking lot', icon: FolderKanban },
  { path: '/kanban', label: 'Kanban', description: 'Plan boards', icon: Sheet },
  { path: '/processes', label: 'Processes', description: 'Internal docs', icon: BookOpen },
  { path: '/journal', label: 'Journal', description: 'Delivered work log', icon: Award },
  { path: '/office', label: 'Office', description: 'Attendance & check-in', icon: Calendar },
  { path: '/people', label: 'People', description: 'Teams & contacts', icon: Users, requiresPeopleAccess: true }
];

const adminCategories = [
  {
    id: 'structure',
    label: 'Structure & Scope',
    icon: Building2,
    tabs: [
      { id: 'workspaces', label: 'Workspaces', icon: Briefcase, description: 'Manage workspaces & members' },
      { id: 'agencies', label: 'Agencies', icon: Briefcase, description: 'Manage agencies' }
    ]
  },
  {
    id: 'contacts',
    label: 'Contacts & Teams',
    icon: Users,
    tabs: [
      { id: 'people', label: 'People', icon: Users, description: 'Manage contacts' },
      { id: 'roles', label: 'Roles', icon: Tag, description: 'Define people roles' },
      { id: 'people_clients', label: 'Clients', icon: Building2, description: 'Manage clients' },
      { id: 'people_teams', label: 'Teams', icon: UsersRound, description: 'Manage teams' }
    ]
  },
  {
    id: 'permissions',
    label: 'Permissions & Access',
    icon: Shield,
    tabs: [
      { id: 'access', label: 'Project Access', icon: Shield, description: 'Assign users to projects' },
      { id: 'people_access', label: 'User Permissions', icon: Users, description: 'Manage user access levels' }
    ]
  },
  {
    id: 'cognitive',
    label: 'Cognitive AI',
    icon: Bot,
    tabs: [
      { id: 'skills', label: 'Carlos Skills', icon: Sparkles, description: 'Manage Carlos Mario skills' },
      { id: 'agents', label: 'AI Agents', icon: Bot, description: 'Configure agent prompts' }
    ]
  },
  {
    id: 'analytics',
    label: 'Reports & Analytics',
    icon: BarChart3,
    tabs: [
      { id: 'office_bi', label: 'Office Analytics', icon: BarChart3, description: 'BI report for daily check-ins' }
    ]
  }
];


function AppLayout({
  user,
  onLogout,
  onChangePassword,
  workspaces = [],
  activeWorkspace,
  onWorkspaceChange,
  children
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const userInitial = user?.name?.charAt(0)?.toUpperCase() || '?';
  const mainRef = useRef(null);

  const isWorkspaceOwner = workspaces.some(ws => ws.owner_contact_id && ws.owner_contact_id === user?.contact_id);

  const filteredAdminCategories = React.useMemo(() => {
    if (user?.is_admin) return adminCategories;
    if (isWorkspaceOwner) {
      return [
        {
          id: 'structure',
          label: 'Structure & Scope',
          icon: Building2,
          tabs: [
            { id: 'workspaces', label: 'Workspaces', icon: Briefcase, description: 'Manage workspaces & members' }
          ]
        }
      ];
    }
    return [];
  }, [user, isWorkspaceOwner]);

  // Toggle a class on .app-main when its direct child opts into full-width
  // layout via data-fullwidth="true". Replaces the global :has() CSS rule,
  // which silently fails in some build/browser combinations.
  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    const hasFullwidth = !!main.querySelector(':scope > [data-fullwidth="true"]');
    main.classList.toggle('app-main--fullwidth', hasFullwidth);
  });

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="app-header-inner">
          <div className="app-brand" style={{ display: 'flex', alignItems: 'center' }}>
            <span className="app-logo">
              <img src="/logo.png" alt="MANAGE!:" />
            </span>
            <div className="app-brand-text">
              <h1>MANAGE!:</h1>
            </div>

            {/* Elegant glassmorphic Workspace Selector */}
            {workspaces.length > 0 && (
              <div style={{
                marginLeft: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(240, 244, 255, 0.8)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                border: '1px solid rgba(102, 126, 234, 0.15)',
                borderRadius: '20px',
                padding: '6px 16px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)',
                transition: 'all 0.3s ease'
              }}>
                <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#667eea', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Workspace
                </span>
                <select
                  value={activeWorkspace?.id || ''}
                  onChange={(e) => onWorkspaceChange(e.target.value)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    fontSize: '0.85rem',
                    fontWeight: '700',
                    color: '#1a1a2e',
                    cursor: 'pointer',
                    outline: 'none',
                    paddingRight: '4px',
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
                  }}
                >
                  {workspaces.map(ws => (
                    <option key={ws.id} value={ws.id} style={{ color: '#1a1a2e', background: '#fff', fontWeight: '500' }}>
                      {ws.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="app-header-actions">
            <div className="app-header-links">
              <button className="header-link" onClick={() => navigate('/preferences')} type="button">
                Preferences
              </button>
              {(user?.is_admin || workspaces.some(ws => ws.owner_contact_id && ws.owner_contact_id === user?.contact_id)) && (
                <button className="header-link" onClick={() => navigate('/admin')} type="button">
                  Admin
                </button>
              )}
              <button className="header-link" onClick={onChangePassword} type="button">
                Change Password
              </button>
            </div>

            <div className="app-account">
              <div className="app-avatar">{userInitial}</div>
              <div className="app-account-text">
                <span className="app-account-name">{user?.name}</span>
                <span className="app-account-status">Signed in</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="app-shell-body">
        <aside className={`app-sidebar${isCollapsed ? ' collapsed' : ''}`}>
          <nav className="app-nav">
            {!isCollapsed && <span className="app-nav-title">Main Menu</span>}
            <ul className="app-nav-list">
              {navItems
                .filter((item) => {
                  if (item.adminOnly) return user?.is_admin;
                  if (item.requiresPeopleAccess) return user?.is_admin || user?.can_view_people;
                  return true;
                })
                .map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      end={item.path === '/'}
                      title={isCollapsed ? item.label : undefined}
                      className={({ isActive }) =>
                        `app-nav-link${isActive ? ' active' : ''}${isCollapsed ? ' collapsed' : ''}`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <Icon className="app-nav-icon" size={20} />
                          {!isCollapsed && (
                            <span className="app-nav-text">
                              <span className="app-nav-label">{item.label}</span>
                              <span className="app-nav-description">{item.description}</span>
                            </span>
                          )}
                          {isCollapsed && isActive && <span className="app-nav-active-indicator" />}
                        </>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>

            {filteredAdminCategories.length > 0 && (
              <>
                {!isCollapsed ? (
                  <span className="app-nav-title" style={{ marginTop: '24px', borderTop: '1px solid #e5e5e5', paddingTop: '16px' }}>
                    Admin Area
                  </span>
                ) : (
                  <div style={{ height: '1px', background: '#e5e5e5', margin: '16px 8px' }} />
                )}

                {filteredAdminCategories.map((category, catIndex) => {
                  const CatIcon = category.icon;
                  return (
                    <div key={category.id} style={{ marginTop: '12px' }}>
                      {!isCollapsed ? (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          color: 'rgba(0, 0, 0, 0.4)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          padding: '0 8px 6px',
                        }}>
                          <CatIcon size={12} />
                          <span>{category.label}</span>
                        </div>
                      ) : (
                        catIndex > 0 && (
                          <div style={{ height: '1px', background: '#f0f0f0', margin: '12px 10px', opacity: 0.6 }} />
                        )
                      )}

                      <ul className="app-nav-list" style={{ gap: '2px' }}>
                        {category.tabs.map((tab) => {
                          const Icon = tab.icon;
                          const tabPath = `/admin?tab=${tab.id}`;
                          const isTabActive = location.pathname === '/admin' && (
                            location.search === `?tab=${tab.id}` ||
                            (tab.id === 'workspaces' && (location.search === '' || location.search === '?tab=workspaces'))
                          );

                          return (
                            <li key={tab.id}>
                              <NavLink
                                to={tabPath}
                                title={isCollapsed ? tab.label : undefined}
                                className={`app-nav-link${isTabActive ? ' active' : ''}${isCollapsed ? ' collapsed' : ''}`}
                              >
                                <Icon className="app-nav-icon" size={20} />
                                {!isCollapsed && (
                                  <span className="app-nav-text">
                                    <span className="app-nav-label">{tab.label}</span>
                                    <span className="app-nav-description">{tab.description}</span>
                                  </span>
                                )}
                                {isCollapsed && isTabActive && <span className="app-nav-active-indicator" />}
                              </NavLink>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </>
            )}
          </nav>

          <div className="app-sidebar-footer">
            <button className="sidebar-button" onClick={onLogout} type="button" title="Logout">
              <LogOut size={16} />
              {!isCollapsed && <span>Logout</span>}
            </button>
            <button
              className="sidebar-button"
              onClick={() => setIsCollapsed(!isCollapsed)}
              type="button"
            >
              {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
              {!isCollapsed && <span>Collapse</span>}
            </button>
          </div>
        </aside>

        <div className={`app-content${isCollapsed ? ' collapsed' : ''}`}>
          <main className="app-main" ref={mainRef}>{children}</main>
        </div>
      </div>
    </div>
  );
}

export default AppLayout;
